// Copyright (c) 2026 WiseTime. All rights reserved.

import { createClient, Interceptor } from "@connectrpc/connect"
import { createGrpcWebTransport } from "@connectrpc/connect-web"
import { createExecutors, isConnectError, from, ExecutorsConfig } from "@wisetime-io/connect-rx"
import { getBaseUrl, getLoginPath } from "./routing/urls"
import { isDevelopmentEnv } from "./config"
import { storeDispatch } from "./storeRef"
import { fetchUserDetails } from "../features/user-context/actions"
import { MembershipService } from "../generated/legebill/site/membership/v4/membership_pb"
import { UserService } from "../generated/wisetime/account/user/v4/user_pb"
import { TimelineService } from "../generated/wisetime/user/timeline/v4/timeline_pb"
import { TagService } from "../generated/legebill/site/tag/v4/tag_pb"
import { SiteService } from "../generated/legebill/site/v4/site_pb"
import { PreferencesService } from "../generated/wisetime/user/preferences/v4/preferences_pb"
import { UserCuesService } from "../generated/wisetime/user/cues/v4/user_cues_pb"
import { AssistantService } from "../generated/wisetime/user/assistant/v4/assistant_pb"
import { NotificationService } from "../generated/wisetime/user/notification/v4/notification_pb"
import { UserTimelineMessageService } from "../generated/wisetime/user/timelinemessage/v4/user_timeline_message_pb"
import { UserTeamService } from "../generated/wt/svc/customer/user_team_pb"
import { NarratePreferencesService } from "../generated/wisetime/user/assistant/v4/preferences_pb"
import { TimesheetFieldService } from "../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"
import { PostExpectationService } from "../generated/legebill/site/postexpectation/v4/post_expectation_pb"

export const redirectToLogin = (): void => window.location.replace(getLoginPath())

const injectTimelineVersionMetadata: Interceptor = (next) => async (req) => {
  const timelineVersionKey = "X-Timeline-Version"
  const buildInfoId = "build-info"
  const timelineVersion = document.getElementById(buildInfoId)?.textContent ?? ""
  req.header.set(timelineVersionKey, timelineVersion)
  return await next(req)
}

const transport = createGrpcWebTransport({
  baseUrl: getBaseUrl(),
  fetch: isDevelopmentEnv()
    ? (input, init) => fetch(input, { ...init, credentials: "include" })
    : undefined,
  interceptors: [injectTimelineVersionMetadata],
})

export const sharedExecutorsConfig: ExecutorsConfig = {
  refreshUrl: getBaseUrl() + "/customer/token",
  onRefreshSuccess: () => storeDispatch(fetchUserDetails()),
  onRefreshFailure: () => redirectToLogin(),
  withCredentials: isDevelopmentEnv(),
}
export const {
  executor,
  executorMutation,
  executorNoRefreshSuccessCallback,
  refreshInFlight,
} = createExecutors({
  ...sharedExecutorsConfig,
})

export const timelineService = createClient(TimelineService, transport)
export const userService = createClient(UserService, transport)
export const membershipService = createClient(MembershipService, transport)
export const tagService = createClient(TagService, transport)
export const siteService = createClient(SiteService, transport)
export const preferencesService = createClient(PreferencesService, transport)
export const userCuesService = createClient(UserCuesService, transport)
export const notificationService = createClient(NotificationService, transport)
export const assistantService = createClient(AssistantService, transport)
export const postExpectationService = createClient(PostExpectationService, transport)
export const userTimelineMessageService = createClient(UserTimelineMessageService, transport)
export const userTeamService = createClient(UserTeamService, transport)
export const narratePreferencesService = createClient(NarratePreferencesService, transport)
export const timesheetFieldService = createClient(TimesheetFieldService, transport)

export { isConnectError, from }
export { Code } from "@connectrpc/connect"
export type { ConnectError } from "@connectrpc/connect"
export type { MessageInitShape as Init } from "@bufbuild/protobuf"
